// TokenomicsDashboard — real-time on-chain metrics for $R₿L on /shadow/tokens.
// Primary data: GeckoTerminal (indexes the main Uniswap V3 pool) + Blockscout
// (free holders count, no API key). DexScreener used as price fallback.
// Self-refreshes every 60s; on network error keeps last known data ("cached").
import React, { useState, useEffect, useRef, useCallback } from "react";
import { ExternalLink, ShieldCheck, BarChart3, Lock } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

const CONTRACT = "0xf9214b42c4c53791846E90066E52D7306adFACf5";
const TOTAL_SUPPLY = 1_000_000_000;
const BURN_ADDRESS = "0x000000000000000000000000000000000000dEaD";
const BASE_RPC = "https://mainnet.base.org";
const FALLBACK_HOLDERS = 9; // verified on basescan.org/token/...#balances
const GECKO_POOLS = `https://api.geckoterminal.com/api/v2/networks/base/tokens/${CONTRACT}/pools?page=1`;
const BLOCKSCOUT_TOKEN = `https://base.blockscout.com/api/v2/tokens/${CONTRACT}`;
const DEXSCREENER = `https://api.dexscreener.com/latest/dex/tokens/${CONTRACT}`;

const LINKS = {
  basescan: `https://basescan.org/token/${CONTRACT}`,
  basescanHolders: `https://basescan.org/token/${CONTRACT}#balances`,
  dexscreener: `https://dexscreener.com/base/${CONTRACT}`,
  pinklock: "https://www.pinksale.finance/pinklock/base/record/1044713",
};

const T = {
  es: {
    title: "Tokenomics $R₿L", price: "Precio", marketCap: "Market Cap", holders: "Holders",
    volume24h: "Volumen 24h", totalSupply: "Supply total", circulatingSupply: "Circulante",
    lpLocked: "LP bloqueado", lastUpdate: "Actualizado hace", seconds: "s",
    viewContract: "Ver contrato", viewChart: "Ver chart", viewLock: "Ver LP lock",
    cached: "Datos en caché", lpDetail: "1.168 LP · PinkLock hasta 09-06-2027",
    burnedSub: "quemados permanentemente",
  },
  en: {
    title: "Tokenomics $R₿L", price: "Price", marketCap: "Market Cap", holders: "Holders",
    volume24h: "24h volume", totalSupply: "Total supply", circulatingSupply: "Circulating",
    lpLocked: "LP locked", lastUpdate: "Updated", seconds: "s ago",
    viewContract: "View contract", viewChart: "View chart", viewLock: "View LP lock",
    cached: "Cached data", lpDetail: "1,168 LP · PinkLock until 2027-06-09",
    burnedSub: "permanently burned",
  },
  sk: {
    title: "Tokenomika $R₿L", price: "Cena", marketCap: "Trhová kap.", holders: "Držitelia",
    volume24h: "Objem 24h", totalSupply: "Celková zásoba", circulatingSupply: "V obehu",
    lpLocked: "LP uzamknuté", lastUpdate: "Aktualizované pred", seconds: "s",
    viewContract: "Zobraziť kontrakt", viewChart: "Zobraziť graf", viewLock: "Zobraziť LP zámok",
    cached: "Uložené dáta", lpDetail: "1 168 LP · PinkLock do 09.06.2027",
    burnedSub: "trvalo spálené",
  }, de: { title: "Tokenomics $R₿L", price: "Preis", marketCap: "Market Cap", holders: "Holders", volume24h: "Volumen 24h", totalSupply: "Gesamtangebot", circulatingSupply: "Umlaufend", lpLocked: "LP gesperrt", lastUpdate: "Aktualisiert vor", seconds: "s", viewContract: "Vertrag ansehen", viewChart: "Chart ansehen", viewLock: "LP Lock ansehen", cached: "Zwischengespeicherte Daten", lpDetail: "1.168 LP · PinkLock bis 09-06-2027", burnedSub: "dauerhaft verbrannt" }, "zh-CN": { title: "Tokenomics $R₿L", price: "价格", marketCap: "Market Cap", holders: "Holders", volume24h: "24h 交易量", totalSupply: "总供应量", circulatingSupply: "流通量", lpLocked: "LP 已锁定", lastUpdate: "更新于", seconds: "秒", viewContract: "查看合约", viewChart: "查看图表", viewLock: "查看 LP 锁定", cached: "缓存数据", lpDetail: "1.168 LP · PinkLock 至 09-06-2027", burnedSub: "已永久销毁" },
};

const formatPrice = (p) => {
  if (p === 0 || !p) return "$0";
  if (p < 0.000001) return `$${p.toFixed(10).replace(/0+$/, "")}`;
  if (p < 0.0001) return `$${p.toFixed(8).replace(/0+$/, "")}`;
  if (p < 0.01) return `$${p.toFixed(6)}`;
  if (p < 1) return `$${p.toFixed(4)}`;
  return `$${p.toFixed(2)}`;
};
const formatUSD = (n) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};
const formatSupply = (n) => new Intl.NumberFormat("en-US").format(n);

async function fetchMetrics() {
  const out = {};
  // Price / change / volume / mcap — GeckoTerminal (main Uniswap V3 pool)
  try {
    const r = await fetch(GECKO_POOLS);
    const d = await r.json();
    const pools = d?.data || [];
    const top = pools
      .slice()
      .sort((a, b) => parseFloat(b.attributes.reserve_in_usd || 0) - parseFloat(a.attributes.reserve_in_usd || 0))[0];
    if (top) {
      const a = top.attributes;
      out.price = parseFloat(a.base_token_price_usd);
      out.change24h = parseFloat(a.price_change_percentage?.h24 ?? 0);
      out.volume24h = pools.reduce((s, p) => s + parseFloat(p.attributes.volume_usd?.h24 || 0), 0);
      out.marketCap = a.fdv_usd ? parseFloat(a.fdv_usd) : out.price * TOTAL_SUPPLY;
    }
  } catch (_e) { /* fall through to DexScreener */ }

  if (out.price == null) {
    // Fallback: DexScreener (only tracks a secondary pool, better than nothing)
    const r = await fetch(DEXSCREENER);
    const d = await r.json();
    const pair = (d?.pairs || []).sort((a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))[0];
    if (!pair) throw new Error("no price source");
    out.price = parseFloat(pair.priceUsd);
    out.change24h = parseFloat(pair.priceChange?.h24 ?? 0);
    out.volume24h = pair.volume?.h24 ?? 0;
    out.marketCap = pair.marketCap ?? out.price * TOTAL_SUPPLY;
  }

  // Holders — refreshed every 60s with the rest of the metrics.
  // 1) Basescan/Etherscan V2 `tokeninfo` if an API key is configured.
  // 2) Blockscout public count, only when it exceeds the verified baseline
  //    (Blockscout's indexer undercounts vs Basescan).
  // 3) Verified Basescan baseline (9) as final fallback, linked to #balances.
  const apiKey = process.env.REACT_APP_BASESCAN_API_KEY;
  if (apiKey) {
    try {
      const r = await fetch(
        `https://api.etherscan.io/v2/api?chainid=8453&module=token&action=tokeninfo&contractaddress=${CONTRACT}&apikey=${apiKey}`
      );
      const d = await r.json();
      const hc = d?.result?.[0]?.holdersCount;
      if (d?.status === "1" && hc != null) out.holders = parseInt(hc, 10);
    } catch (_e) { /* try next source */ }
  }
  if (out.holders == null) {
    try {
      const r = await fetch(BLOCKSCOUT_TOKEN);
      const d = await r.json();
      const raw = d?.holders_count ?? d?.holders;
      const n = raw != null ? parseInt(raw, 10) : null;
      if (n != null && n > FALLBACK_HOLDERS) out.holders = n;
    } catch (_e) { /* fall through */ }
  }
  if (out.holders == null) out.holders = FALLBACK_HOLDERS;

  // Circulating supply — on-chain truth = totalSupply − balanceOf(0x...dEaD).
  // Queries Base RPC directly (free, public). Refreshes every 60s automatically.
  try {
    const padded = "70a08231" + "000000000000000000000000" + BURN_ADDRESS.slice(2).toLowerCase();
    const r = await fetch(BASE_RPC, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        method: "eth_call",
        params: [{ to: CONTRACT, data: "0x" + padded }, "latest"],
        id: 1,
      }),
    });
    const d = await r.json();
    if (d?.result) {
      const burnedWei = BigInt(d.result);
      const burnedRBL = Number(burnedWei / 10n ** 18n);
      out.burnedAtDead = burnedRBL;
      out.circulating = Math.max(0, TOTAL_SUPPLY - burnedRBL);
    }
  } catch (_e) { /* keep undefined → UI shows TOTAL_SUPPLY fallback */ }

  return out;
}

function MetricCard({ label, value, sub, testId, loading }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-xl bg-white/[0.04] px-4 py-3.5 ${loading ? "animate-pulse" : ""}`}
    >
      <p className="text-[11px] font-mono uppercase tracking-[0.16em] text-white/40">{label}</p>
      <p className="mt-1 text-[22px] font-medium leading-tight text-white">
        {loading ? <span className="inline-block h-6 w-20 rounded bg-white/10" /> : value}
      </p>
      {!loading && sub}
    </div>
  );
}

export default function TokenomicsDashboard() {
  const { lang } = useLanguage();
  const t = T[lang] || T.en || T.es;

  const [data, setData] = useState(null);
  const [isCached, setIsCached] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [, forceTick] = useState(0);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const m = await fetchMetrics();
      if (!mountedRef.current) return;
      setData(m);
      setIsCached(false);
      setUpdatedAt(Date.now());
    } catch (_e) {
      if (!mountedRef.current) return;
      setIsCached(true); // keep last known data
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    const fetchId = setInterval(load, 60_000);
    const tickId = setInterval(() => forceTick((n) => n + 1), 5_000); // refresh "X s ago"
    return () => {
      mountedRef.current = false;
      clearInterval(fetchId);
      clearInterval(tickId);
    };
  }, [load]);

  const loading = data == null && !isCached;
  const agoSec = updatedAt ? Math.max(0, Math.round((Date.now() - updatedAt) / 1000)) : null;
  const ch = data?.change24h ?? 0;

  return (
    <div
      data-testid="tokenomics-dashboard"
      className="rounded-2xl border-[0.5px] border-white/15 bg-white/[0.02] p-5 backdrop-blur"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-white">{t.title}</h3>
        <div className="flex items-center gap-2">
          {isCached && (
            <span
              data-testid="tok-cached-badge"
              className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-wider text-amber-300"
            >
              {t.cached}
            </span>
          )}
          {agoSec != null && (
            <span data-testid="tok-updated" className="text-[10px] font-mono text-white/35">
              {t.lastUpdate} {agoSec}{t.seconds}
            </span>
          )}
        </div>
      </div>

      {/* Row 1 — live market metrics */}
      <div className="mt-4 grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
        <MetricCard
          label={t.price} testId="tok-price" loading={loading}
          value={data?.price != null ? formatPrice(data.price) : "—"}
          sub={data?.price != null && (
            <p className={`mt-0.5 text-xs font-mono font-bold ${ch >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {ch >= 0 ? "+" : ""}{ch.toFixed(2)}% 24h
            </p>
          )}
        />
        <MetricCard
          label={t.marketCap} testId="tok-mcap" loading={loading}
          value={data?.marketCap != null ? formatUSD(data.marketCap) : "—"}
        />
        <MetricCard
          label={t.holders} testId="tok-holders" loading={loading}
          value={data?.holders != null ? formatSupply(data.holders) : "—"}
          sub={
            <a href={LINKS.basescanHolders} target="_blank" rel="noopener noreferrer"
              className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-mono text-amber-300/80 hover:text-amber-300">
              Basescan <ExternalLink className="h-2.5 w-2.5" />
            </a>
          }
        />
        <MetricCard
          label={t.volume24h} testId="tok-volume" loading={loading}
          value={data?.volume24h != null ? formatUSD(data.volume24h) : "—"}
        />
      </div>

      {/* Row 2 — supply (circulating only, decreases live with each burn) */}
      <div className="mt-2 grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
        <MetricCard
          label={t.circulatingSupply}
          testId="tok-supply-circ"
          loading={loading && data?.circulating == null}
          value={formatSupply(data?.circulating ?? TOTAL_SUPPLY)}
          sub={data?.burnedAtDead != null ? (
            <p className="mt-0.5 text-[10px] font-mono text-orange-300/80">
              🔥 {data.burnedAtDead.toLocaleString("es-ES")} R₿L {t.burnedSub}
            </p>
          ) : null}
        />
        <MetricCard
          label={t.lpLocked} testId="tok-lp-locked" loading={false} value="1,168 LP"
          sub={<p className="mt-0.5 text-[10px] font-mono text-white/40">{t.lpDetail}</p>}
        />
      </div>

      {/* Row 3 — transparency links */}
      <div className="mt-3 flex flex-wrap gap-2">
        {[
          { h: LINKS.basescan, l: t.viewContract, I: ShieldCheck, id: "tok-link-basescan" },
          { h: LINKS.dexscreener, l: t.viewChart, I: BarChart3, id: "tok-link-dexscreener" },
          { h: LINKS.pinklock, l: t.viewLock, I: Lock, id: "tok-link-pinklock" },
        ].map(({ h, l, I, id }) => (
          <a key={id} href={h} target="_blank" rel="noopener noreferrer" data-testid={id}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-white/60 transition-colors hover:bg-white/[0.06] hover:text-white">
            <I className="h-3.5 w-3.5" /> {l} <ExternalLink className="h-3 w-3 opacity-50" />
          </a>
        ))}
      </div>
    </div>
  );
}
